import AlertBanner from '@rsa-digital/evo-shared-components/components/AlertBanner';
import { graphql, useStaticQuery } from 'gatsby';
import React, { useState } from 'react';
import { processOptionalCta } from 'helpers/csTypeProcessors';
import { CsCta, CsIcon } from 'types/contentStack';
import { AlertBannerSection } from './styles';

type AlertBannersData = {
  csAlertBanners: {
    show_help_text: string;
    hide_help_text: string;
    alert_banners: CsAlertBanner[];
  };
};

type CsAlertBanner = {
  emergency_variant: boolean;
  display_globally: boolean;
  closable: boolean;
  collapsible: boolean;
  collapsed_by_default: boolean;
  heading: string;
  supporting_text?: string;
  left_paragraph?: string;
  right_paragraph?: string;
  cta: [CsCta] | null;
  icon: [CsIcon];
  included_pages: {
    url: string;
  }[];
};

type AlertBannersProps = {
  pageUrl: string;
};

const alertBannersQuery = graphql`
  query {
    csAlertBanners {
      show_help_text
      hide_help_text
      alert_banners {
        emergency_variant
        display_globally
        closable
        heading
        supporting_text
        left_paragraph
        right_paragraph
        collapsible
        collapsed_by_default
        icon {
          icon_code
        }
        included_pages {
          url
        }
        cta {
          display_text
          screen_reader_text
          url
        }
      }
    }
  }
`;

const stripLeadingAndTrailingSlashes = (url: string): string =>
  url.replace(/(^\/)|(\/$)/g, '');

const AlertBanners: React.FC<AlertBannersProps> = ({ pageUrl }) => {
  const alertBannerData =
    useStaticQuery<AlertBannersData>(alertBannersQuery).csAlertBanners;

  const [closedBanners, setClosedBanners] = useState<string[]>([]);

  if (!alertBannerData) {
    return null;
  }

  const applicableBanners = alertBannerData.alert_banners.filter(
    (banner) =>
      banner.display_globally ||
      banner.included_pages
        .map((includedPages) => stripLeadingAndTrailingSlashes(includedPages.url))
        .includes(stripLeadingAndTrailingSlashes(pageUrl))
  );

  const orderedBanners = [
    ...applicableBanners.filter((banner) => banner.emergency_variant),
    ...applicableBanners.filter((banner) => !banner.emergency_variant),
  ];

  const handleClose = (heading: string): void => {
    setClosedBanners([...closedBanners, heading]);
  };

  const allBannersClosed = closedBanners.length === orderedBanners.length;

  return orderedBanners.length >= 1 && !allBannersClosed ? (
    <AlertBannerSection role="region">
      {orderedBanners.map((banner, index) => {
        const optionalCta = processOptionalCta(banner.cta, 'alertBannerCta');
        const processedIcon = banner.icon[0].icon_code;
        return (
          <div key={`${banner.heading}-${index}`} data-cy={`AlertBanner${index}`}>
            <AlertBanner
              emergencyVariant={banner.emergency_variant}
              closable={banner.closable}
              collapsible={banner.collapsible}
              collapsedByDefault={banner.collapsed_by_default}
              heading={banner.heading}
              handleClose={() => handleClose(banner.heading)}
              supportingText={banner.supporting_text}
              leftParagraph={banner.left_paragraph}
              rightParagraph={banner.right_paragraph}
              cta={optionalCta}
              icon={processedIcon}
              showHelpText={alertBannerData.show_help_text}
              hideHelpText={alertBannerData.hide_help_text}
            />
          </div>
        );
      })}
    </AlertBannerSection>
  ) : (
    <></>
  );
};

export default AlertBanners;
